import { Button, ButtonProps } from "../Button";
import { useCalendar } from "./CalendarContext";
import { MouseEvent, forwardRef, useEffect, useState } from "react";
import Prev from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_left.svg";
import styles from "./Calendar.module.scss";
import { isSameMonth } from "./internal/utils";
import cx from "classnames";

export type CalendarPrevProps = ButtonProps;

/**
 * A button that displays the previous month and allows the user to select a different month.
 *
 * @param props.onClick - The function to call when the button is clicked.
 * @param props.className - The class name to apply to the button.
 * @param props - The rest of the props for the Button component.
 * @returns A button that displays the previous month and allows the user to select a different month.
 */
export const CalendarPrev = forwardRef<HTMLButtonElement, CalendarPrevProps>(
  (props, ref) => {
    const { onClick, className, ...rest } = props;
    const {
      focusedDate,
      setFocusedDate,
      minDate,
      setSelectedMonth,
      setSelectedYear,
    } = useCalendar();

    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
      setDisabled(
        minDate != null &&
          focusedDate.minus({ month: 1 }).endOf("month") < minDate,
      );
    }, [minDate, focusedDate]);

    const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);

      const targetMonth = focusedDate.minus({ month: 1 }).endOf("month");
      const targetDate = focusedDate.minus({ month: 1 });

      // Check if we're crossing year boundary (January to December)
      const isCrossingYearBoundary =
        focusedDate.month === 1 && targetDate.month === 12;

      if (minDate !== null) {
        if (targetDate.toMillis() >= minDate?.toMillis()) {
          setFocusedDate(targetDate);
          setSelectedMonth(targetDate.month);
          if (isCrossingYearBoundary) {
            setSelectedYear(targetDate.year);
          }
          return;
        }

        if (!isSameMonth(targetMonth, focusedDate)) {
          setFocusedDate(minDate);
          setSelectedMonth(minDate.month);
          if (isCrossingYearBoundary) {
            setSelectedYear(minDate.year);
          }
        }
        return;
      }
      setFocusedDate(targetDate);
      setSelectedMonth(targetDate.month);
      if (isCrossingYearBoundary) {
        setSelectedYear(targetDate.year);
      }
    };

    return (
      <Button
        ref={ref}
        onClick={onClickHandler}
        className={cx(styles["controller-button"], className)}
        appearance="ghost"
        size="small"
        icon={Prev}
        data-anv="calendar-prev"
        aria-label="Previous Month"
        {...rest}
        disabled={disabled}
      />
    );
  },
);

CalendarPrev.displayName = "CalendarPrevButton";
